import type {
  ConcluirOauthGoogle,
  ConectarKommo,
  MapeamentoDoFunil,
  RecorteDoProjeto,
  SalvarConexaoGoogle,
  SalvarConexaoMeta,
  SalvarCredencialGoogle,
  DefinirMonitoramentoDaConta,
  SalvarMinimosDeSaldo,
} from "@base/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import type { ConexoesGateway } from "../infrastructure/conexoes.gateway";

const Contexto = createContext<ConexoesGateway | null>(null);

export const chavesDeConexoes = {
  kommo: (clienteId: string) => ["conexoes", "kommo", clienteId] as const,
  meta: (clienteId: string) => ["conexoes", "meta", clienteId] as const,
  contasMeta: (clienteId: string) => ["conexoes", "meta", clienteId, "contas"] as const,
  credencialMeta: ["conexoes", "meta", "credencial"] as const,
  google: (clienteId: string) => ["conexoes", "google", clienteId] as const,
  contasGoogle: (clienteId: string) => ["conexoes", "google", clienteId, "contas"] as const,
  credencialGoogle: ["conexoes", "google", "credencial"] as const,
  chaveDeIa: ["conexoes", "openai", "credencial"] as const,
  saldos: ["conexoes", "saldos"] as const,
};

/** Injecao do gateway: HTTP em producao, memoria no teste. */
export function ProvedorDeConexoes({
  gateway,
  children,
}: {
  readonly gateway: ConexoesGateway;
  readonly children: ReactNode;
}) {
  return <Contexto.Provider value={gateway}>{children}</Contexto.Provider>;
}

function useConexoesGateway(): ConexoesGateway {
  const gateway = useContext(Contexto);
  if (!gateway) throw new Error("useConexoesGateway precisa estar dentro de <ProvedorDeConexoes>.");
  return gateway;
}

/**
 * Conexao com o Kommo. Enquanto sincroniza, consulta de novo a cada 3 s; fora
 * disso, a cada minuto, para perceber a sincronizacao de hora em hora.
 */
export function useConexaoKommo(clienteId: string) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.kommo(clienteId),
    queryFn: () => gateway.kommo(clienteId),
    refetchInterval: (consulta) => (consulta.state.data?.situacao === "sincronizando" ? 3_000 : 60_000),
  });
}

/**
 * Toda escrita na conexao muda tambem os numeros do Dashboard: invalida os dois.
 */
function useAtualizarDepois(clienteId: string) {
  const cliente = useQueryClient();
  return () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: chavesDeConexoes.kommo(clienteId) }),
      cliente.invalidateQueries({ queryKey: ["dashboard", clienteId] }),
    ]);
}

export function useConectarKommo(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: (dados: ConectarKommo) => gateway.conectarKommo(clienteId, dados),
    onSuccess: () => atualizar(),
  });
}

export function useMapearKommo(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: (mapeamento: MapeamentoDoFunil) => gateway.mapearKommo(clienteId, mapeamento),
    onSuccess: () => atualizar(),
  });
}

export function useSincronizarKommo(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: () => gateway.sincronizarKommo(clienteId),
    onSuccess: () => atualizar(),
  });
}

export function useDesconectarKommo(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: () => gateway.desconectarKommo(clienteId),
    onSuccess: () => atualizar(),
  });
}

export function useDefinirRecorte(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: (recorte: RecorteDoProjeto | null) => gateway.definirRecorte(clienteId, recorte),
    onSuccess: () => atualizar(),
  });
}

export function useDefinirCamposDoFiltro(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarDepois(clienteId);
  return useMutation({
    mutationFn: (campos: readonly number[]) => gateway.definirCamposDoFiltro(clienteId, campos),
    onSuccess: () => atualizar(),
  });
}

/** Acesso da Monte ao Meta (um so para todas as clinicas). */
export function useCredencialMeta() {
  const gateway = useConexoesGateway();
  return useQuery({ queryKey: chavesDeConexoes.credencialMeta, queryFn: () => gateway.credencialMeta() });
}

export function useSalvarCredencialMeta() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => gateway.salvarCredencialMeta(token),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["conexoes", "meta"] }),
  });
}

/** Conexao da clinica com o Meta. Enquanto sincroniza, consulta de novo a cada 3 s. */
export function useConexaoMeta(clienteId: string) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.meta(clienteId),
    queryFn: () => gateway.conexaoMeta(clienteId),
    refetchInterval: (consulta) => (consulta.state.data?.situacao === "sincronizando" ? 3_000 : false),
  });
}

export function useContasDeAnuncio(clienteId: string, habilitado: boolean) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.contasMeta(clienteId),
    queryFn: () => gateway.contasDeAnuncio(clienteId),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

function useAtualizarMeta(clienteId: string) {
  const cliente = useQueryClient();
  return () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: chavesDeConexoes.meta(clienteId) }),
      cliente.invalidateQueries({ queryKey: ["dashboard", clienteId] }),
    ]);
}

export function useSalvarConexaoMeta(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarMeta(clienteId);
  return useMutation({
    mutationFn: (dados: SalvarConexaoMeta) => gateway.salvarConexaoMeta(clienteId, dados),
    onSuccess: () => atualizar(),
  });
}

export function useSincronizarMeta(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarMeta(clienteId);
  return useMutation({ mutationFn: () => gateway.sincronizarMeta(clienteId), onSuccess: () => atualizar() });
}

export function useDesconectarMeta(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarMeta(clienteId);
  return useMutation({ mutationFn: () => gateway.desconectarMeta(clienteId), onSuccess: () => atualizar() });
}

export function useCredencialGoogle() {
  const gateway = useConexoesGateway();
  return useQuery({ queryKey: chavesDeConexoes.credencialGoogle, queryFn: () => gateway.credencialGoogle() });
}

export function useSalvarCredencialGoogle() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: SalvarCredencialGoogle) => gateway.salvarCredencialGoogle(dados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["conexoes", "google"] }),
  });
}

export function useIniciarOauthGoogle() {
  const gateway = useConexoesGateway();
  return useMutation({ mutationFn: () => gateway.iniciarOauthGoogle() });
}

export function useConcluirOauthGoogle() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: ConcluirOauthGoogle) => gateway.concluirOauthGoogle(dados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["conexoes", "google"] }),
  });
}

export function useChaveDeIa() {
  const gateway = useConexoesGateway();
  return useQuery({ queryKey: chavesDeConexoes.chaveDeIa, queryFn: () => gateway.chaveDeIa() });
}

export function useSalvarChaveDeIa() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (chave: string) => gateway.salvarChaveDeIa(chave),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesDeConexoes.chaveDeIa }),
  });
}

export function useRemoverChaveDeIa() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.removerChaveDeIa(),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesDeConexoes.chaveDeIa }),
  });
}

export function useRemoverCredencialGoogle() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.removerCredencialGoogle(),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["conexoes", "google"] }),
  });
}

export function useConexaoGoogle(clienteId: string) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.google(clienteId),
    queryFn: () => gateway.conexaoGoogle(clienteId),
  });
}

export function useContasGoogle(clienteId: string, habilitado: boolean) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.contasGoogle(clienteId),
    queryFn: () => gateway.contasGoogle(clienteId),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

function useAtualizarGoogle(clienteId: string) {
  const cliente = useQueryClient();
  return () => cliente.invalidateQueries({ queryKey: chavesDeConexoes.google(clienteId) });
}

export function useSalvarConexaoGoogle(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarGoogle(clienteId);
  return useMutation({
    mutationFn: (dados: SalvarConexaoGoogle) => gateway.salvarConexaoGoogle(clienteId, dados),
    onSuccess: () => atualizar(),
  });
}

export function useDesconectarGoogle(clienteId: string) {
  const gateway = useConexoesGateway();
  const atualizar = useAtualizarGoogle(clienteId);
  return useMutation({ mutationFn: () => gateway.desconectarGoogle(clienteId), onSuccess: () => atualizar() });
}

/**
 * Saldo das contas da Monte. Consulta de novo a cada 5 min para a tela aberta
 * acompanhar a leitura automatica (de hora em hora no servidor).
 */
export function useMonitoramentoDeSaldos(habilitado = true) {
  const gateway = useConexoesGateway();
  return useQuery({
    queryKey: chavesDeConexoes.saldos,
    queryFn: () => gateway.monitoramentoDeSaldos(),
    refetchInterval: 5 * 60_000,
    enabled: habilitado,
  });
}

export function useLerSaldosAgora() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.lerSaldosAgora(),
    onSuccess: (dados) => cliente.setQueryData(chavesDeConexoes.saldos, dados),
  });
}

export function useSalvarMinimosDeSaldo() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: SalvarMinimosDeSaldo) => gateway.salvarMinimosDeSaldo(dados),
    onSuccess: (dados) => cliente.setQueryData(chavesDeConexoes.saldos, dados),
  });
}

export function useDefinirMonitoramentoDaConta() {
  const gateway = useConexoesGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: DefinirMonitoramentoDaConta) => gateway.definirMonitoramentoDaConta(dados),
    onSuccess: (dados) => cliente.setQueryData(chavesDeConexoes.saldos, dados),
  });
}
